import React from 'react';

/**
 * Simple Error Boundary that catches render-time exceptions from its
 * children and shows a friendly fallback instead of a blank white page.
 *
 * Used to wrap the router so any unexpected crash on a page (e.g. malformed
 * data, missing field, etc.) still renders SOMETHING for the user.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '', stack: '' };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      message: (error && (error.message || String(error))) || 'Unknown error',
      stack: (error && error.stack) || '',
    };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught error:', error, info);
  }

  handleReload = () => {
    this.setState({ hasError: false, message: '', stack: '' });
    try { window.location.reload(); } catch { /* noop */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="error-boundary">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-lg shadow-sm p-6">
          <div className="text-2xl mb-2">😥</div>
          <h1 className="font-display text-xl font-semibold text-slate-900 mb-1">Algo salió mal</h1>
          <p className="text-sm text-slate-600 mb-4">
            Ocurrió un error inesperado al renderizar esta página. Puedes intentar recargar.
          </p>
          {this.state.message && (
            <pre className="text-xs bg-slate-50 border border-slate-200 rounded p-2 overflow-x-auto text-slate-700 mb-4" data-testid="error-message">
              {this.state.message}
            </pre>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={this.handleReload}
              className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800"
              data-testid="error-boundary-reload"
            >
              Recargar
            </button>
            <a
              href="/dashboard"
              className="px-3 py-1.5 rounded-md border border-slate-200 text-slate-700 text-sm hover:bg-slate-50"
            >
              Ir al panel
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
